import { jsPDF } from 'jspdf';
import { calculateOverallMaturity } from '@/utils/maturityLevelUtils';
import { getAnswerRecommendations, sortRecommendationsByPriority } from '@/utils/recommendations';
import type { Recommendation } from '@/types/assessment';

interface FreeSurveyResponse {
  assessmentTitle: string;
  isMaturityModel: boolean;
  scoresByCategory: Record<string, { current: number; target: number; categoryName?: string }>;
  maturityScores: Record<string, any>;
  answerDetails: Record<string, any>;
  categories?: Array<{
    id: string;
    name: string;
    description: string;
    questions: Array<{
      id: string;
      text: string;
      answerType: string;
      answers: Array<{
        id: string;
        text: string;
        value: number;
        recommendedActions?: Array<{ id?: string; text: string; priority: string }>;
      }>;
    }>;
  }>;
  submittedAt: string;
  isPremiumReport?: boolean;
}

// Helper: get category name from scores or categories
function getCategoryName(
  categoryId: string,
  scores: Record<string, any>,
  categories?: any[],
  answerDetails?: Record<string, any>
): string {
  // From enriched scores
  if (scores[categoryId]?.categoryName) return scores[categoryId].categoryName;
  // From categories array
  const cat = categories?.find((c: any) => c.id === categoryId);
  if (cat) return cat.name;
  // From answer details
  const detail = Object.values(answerDetails || {}).find((d: any) => d?.categoryId === categoryId) as any;
  if (detail?.categoryName) return detail.categoryName;
  return categoryId.slice(0, 8);
}

// Generate full recommendations from assessment structure
function generateRecommendations(response: FreeSurveyResponse): Recommendation[] {
  if (!response.categories || response.categories.length === 0) return [];

  const reconstructedResponses: Record<string, { questionId: string; current: string[] | number; target: string[] | number }> = {};
  Object.entries(response.answerDetails || {}).forEach(([questionId, detail]: [string, any]) => {
    if (!detail) return;
    let current: string[] | number = [];
    let target: string[] | number = [];
    if (detail.current?.id) current = [detail.current.id];
    else if (detail.current?.value !== undefined) current = detail.current.value;
    if (detail.target?.id) target = [detail.target.id];
    else if (detail.target?.value !== undefined) target = detail.target.value;
    reconstructedResponses[questionId] = { questionId, current, target };
  });

  const cats = response.categories.map(c => ({
    ...c,
    questions: c.questions.map(q => ({
      ...q,
      answerType: q.answerType as any,
      required: true,
      premium: false,
      answers: q.answers.map(a => ({
        ...a,
        recommendedActions: (a.recommendedActions || []).map(ra => ({
          id: ra.id || crypto.randomUUID(),
          text: ra.text,
          priority: ra.priority as any,
        })),
      })),
    }))
  }));

  return sortRecommendationsByPriority(getAnswerRecommendations(cats, reconstructedResponses));
}

export const generateFreeSurveyPDF = async (response: FreeSurveyResponse) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margins = { top: 20, right: 20, bottom: 20, left: 20 };
  const contentWidth = pageWidth - margins.left - margins.right;
  let y = margins.top;

  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - margins.bottom) {
      pdf.addPage();
      y = margins.top;
    }
  };

  // ── Header gradient ──
  const gradientHeight = 60;
  for (let i = 0; i < 50; i++) {
    const ratio = i / 50;
    const r = Math.floor(232 + (249 - 232) * ratio);
    const g = Math.floor(122 + (115 - 122) * ratio);
    const b = Math.floor(46 + (22 - 46) * ratio);
    pdf.setFillColor(r, g, b);
    pdf.rect(0, (gradientHeight / 50) * i, pageWidth, gradientHeight / 50 + 1, 'F');
  }

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Assessment Complete!', pageWidth / 2, 25, { align: 'center' });

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Thank you for completing the ${response.assessmentTitle} Preview!`, pageWidth / 2, 36, { align: 'center' });
  
  const submittedDate = new Date(response.submittedAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
  pdf.setFontSize(9);
  pdf.text(`Submitted: ${submittedDate}`, pageWidth / 2, 44, { align: 'center' });

  y = gradientHeight + 15;
  pdf.setTextColor(0, 0, 0);

  // ── Maturity Summary ──
  if (response.isMaturityModel) {
    // Clean scores for calculation
    const cleanScores: Record<string, { current: number; target: number }> = {};
    Object.entries(response.scoresByCategory).forEach(([id, s]) => {
      cleanScores[id] = { current: s.current, target: s.target };
    });
    const maturityResults = calculateOverallMaturity(cleanScores);
    
    if (maturityResults) {
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Your Assessment Results', margins.left, y);
      y += 12;

      const cardWidth = (contentWidth - 10) / 2;
      const cardHeight = 45;

      // Helper for badge color
      const getBadgeColor = (level: number) => {
        if (level === 0) return { r: 0, g: 0, b: 0 };
        if (level === 1) return { r: 251, g: 191, b: 36 };
        if (level === 2) return { r: 251, g: 146, b: 60 };
        if (level === 3) return { r: 234, g: 88, b: 12 };
        return { r: 34, g: 197, b: 94 };
      };

      // Current card
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margins.left, y, cardWidth, cardHeight, 3, 3, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.roundedRect(margins.left, y, cardWidth, cardHeight, 3, 3, 'S');

      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Current Maturity', margins.left + 5, y + 8);

      pdf.setFontSize(28);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text(maturityResults.current?.score?.toFixed(1) || '0', margins.left + 5, y + 22);

      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Level ${maturityResults.current?.level || 0} - ${maturityResults.current?.label || 'N/A'}`, margins.left + 5, y + 28);

      // Target card
      const targetX = margins.left + cardWidth + 10;
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(targetX, y, cardWidth, cardHeight, 3, 3, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.roundedRect(targetX, y, cardWidth, cardHeight, 3, 3, 'S');

      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Target Maturity', targetX + 5, y + 8);

      pdf.setFontSize(28);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text(maturityResults.target?.score?.toFixed(1) || '0', targetX + 5, y + 22);

      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Level ${maturityResults.target?.level || 0} - ${maturityResults.target?.label || 'N/A'}`, targetX + 5, y + 28);

      y += cardHeight + 15;
    }
  }

  // ── Category Scores ──
  const categoryScores = Object.entries(response.scoresByCategory);
  if (categoryScores.length > 0) {
    checkPageBreak(30);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Scores by Category', margins.left, y);
    y += 10;

    categoryScores.forEach(([categoryId, scores]) => {
      checkPageBreak(18);
      const categoryName = getCategoryName(categoryId, response.scoresByCategory, response.categories, response.answerDetails);

      // Row bg
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margins.left, y - 4, contentWidth, 10, 2, 2, 'F');

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(categoryName, margins.left + 4, y + 2);

      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.setFontSize(9);
      const scoreText = `Current: ${scores.current?.toFixed(1)}  |  Target: ${scores.target?.toFixed(1)}`;
      pdf.text(scoreText, margins.left + contentWidth - 4, y + 2, { align: 'right' });

      y += 4;

      // Score bars
      const barHeight = 4;
      const barWidth = contentWidth - 8;
      const maxScore = 4;

      pdf.setFillColor(229, 231, 235);
      pdf.roundedRect(margins.left + 4, y, barWidth, barHeight, 1, 1, 'F');

      const currentBarW = Math.max(0, (scores.current / maxScore) * barWidth);
      if (currentBarW > 0) {
        pdf.setFillColor(142, 145, 150);
        pdf.roundedRect(margins.left + 4, y, currentBarW, barHeight, 1, 1, 'F');
      }

      const targetPos = (scores.target / maxScore) * barWidth;
      pdf.setDrawColor(249, 115, 22);
      pdf.setLineWidth(0.8);
      pdf.line(margins.left + 4 + targetPos, y - 1, margins.left + 4 + targetPos, y + barHeight + 1);

      y += barHeight + 8;
    });
    y += 5;
  }

  // ── Recommendations (from full assessment structure) ──
  const recommendations = generateRecommendations(response);

  if (recommendations.length > 0) {
    checkPageBreak(30);

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Recommendations', margins.left, y);
    y += 8;

    pdf.setFontSize(10);
    pdf.setTextColor(107, 114, 128);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Based on the gaps between your current and target states', margins.left, y);
    y += 12;

    // Group by category then question
    const grouped: Record<string, Record<string, { questionText: string; recommendations: Recommendation[] }>> = {};
    recommendations.forEach(rec => {
      if (!grouped[rec.categoryName]) grouped[rec.categoryName] = {};
      if (!grouped[rec.categoryName][rec.questionId]) {
        grouped[rec.categoryName][rec.questionId] = { questionText: rec.questionText, recommendations: [] };
      }
      grouped[rec.categoryName][rec.questionId].recommendations.push(rec);
    });

    Object.entries(grouped).forEach(([categoryName, questionGroups]) => {
      checkPageBreak(25);

      const totalRecs = Object.values(questionGroups).reduce((sum, g) => sum + g.recommendations.length, 0);

      // Category header
      pdf.setDrawColor(229, 231, 235);
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margins.left, y, contentWidth, 10, 2, 2, 'FD');

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(categoryName, margins.left + 3, y + 6);

      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`(${totalRecs} recommendation${totalRecs !== 1 ? 's' : ''})`, contentWidth + margins.left - 35, y + 6);

      y += 16;

      Object.entries(questionGroups).forEach(([, group]) => {
        checkPageBreak(15);

        // Question text with left border
        pdf.setDrawColor(224, 224, 224);
        pdf.setLineWidth(1);
        pdf.line(margins.left + 3, y - 2, margins.left + 3, y + (group.recommendations.length * 5) + 3);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const questionLines = pdf.splitTextToSize(group.questionText, contentWidth - 15);
        pdf.text(questionLines, margins.left + 7, y);
        y += Math.max(questionLines.length * 4, 5);

        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text(`(${group.recommendations.length} recommendation${group.recommendations.length !== 1 ? 's' : ''})`, margins.left + 7, y);
        y += 5;

        group.recommendations.forEach(rec => {
          checkPageBreak(8);

          // Priority badge
          const priorityColors: Record<string, { r: number; g: number; b: number }> = {
            high: { r: 239, g: 68, b: 68 },
            medium: { r: 249, g: 115, b: 22 },
            low: { r: 34, g: 197, b: 94 },
          };
          const color = priorityColors[rec.priority] || priorityColors.medium;

          pdf.setFillColor(color.r, color.g, color.b);
          pdf.roundedRect(margins.left + 7, y - 3, 16, 5, 1.5, 1.5, 'F');
          pdf.setFontSize(7);
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          pdf.text(rec.priority.toUpperCase(), margins.left + 15, y, { align: 'center' });

          pdf.setFontSize(9);
          pdf.setTextColor(107, 114, 128);
          pdf.setFont('helvetica', 'normal');
          const recLines = pdf.splitTextToSize(`• ${rec.text}`, contentWidth - 30);
          pdf.text(recLines, margins.left + 26, y);
          y += recLines.length * 4 + 2;
        });

        y += 3;
      });

      y += 5;
    });
  }

  if (!response.isPremiumReport) {
    // ── Next Steps Section ──
    checkPageBreak(40);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('What happens next?', margins.left, y);
    y += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(107, 114, 128);

    const nextSteps = [
      "• Your responses have been recorded",
      "• Review the recommendations above to improve your maturity scores",
      "• If you want access to the full assessment, please request premium access"
    ];

    nextSteps.forEach(step => {
      checkPageBreak(15);
      const stepLines = pdf.splitTextToSize(step, contentWidth - 30);
      pdf.text(stepLines, margins.left + 15, y);
      y += stepLines.length * 5 + 2;
    });
  }

  // ── Footer on every page ──
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text(`Generated on ${new Date().toLocaleDateString()} • Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  pdf.save(`${response.assessmentTitle} - Report.pdf`);
};
